import React, { useState } from 'react';
import { Modal, Button, Input } from 'glintly-ui';
import styles from './profile.module.css';
import { toast } from 'react-toastify';
const BankDetailsModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    bankName: '',
    accountNumber: '',
    ifsc: '',
    accountHolderName: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    if (!formData.accountNumber) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!formData.ifsc.trim()) {
      newErrors.ifsc = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.toUpperCase())) {
      newErrors.ifsc = 'Please enter a valid IFSC code (e.g., SBIN0001234)';
    }

    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Convert IFSC to uppercase
      const submitData = {
        ...formData,
        ifsc: formData.ifsc.toUpperCase(),
      };

      await onSubmit(submitData);
      toast.success('Bank details submitted successfully!');
      onClose();
      // Reset form
      setFormData({
        bankName: '',
        accountNumber: '',
        ifsc: '',
        accountHolderName: '',
      });
      setErrors({});
    } catch (error) {
      toast.error(error.message || 'Failed to submit bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      bankName: '',
      accountNumber: '',
      ifsc: '',
      accountHolderName: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal size="lg" isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>Add Bank Details</Modal.Header>
      <Modal.Body>
        <div className={styles.modalContent}>
          <form onSubmit={handleSubmit} className={styles.modalForm}>
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Bank Information</h3>

              <div className={styles.formGroup}>
                <Input.Root name="bankName" required errorMessage={errors.bankName}>
                  <Input.Label>Bank Name</Input.Label>
                  <Input.Control
                    type="text"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    placeholder="Enter bank name (e.g., State Bank of India)"
                  />
                </Input.Root>
              </div>

              <div className={styles.formGroup}>
                <Input.Root name="accountNumber" required errorMessage={errors.accountNumber}>
                  <Input.Label>Account Number</Input.Label>
                  <Input.Control
                    type="text"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    placeholder="Enter account number"
                    maxLength={18}
                  />
                </Input.Root>
              </div>

              <div className={styles.formGroup}>
                <Input.Root name="ifsc" required errorMessage={errors.ifsc}>
                  <Input.Label>IFSC Code</Input.Label>
                  <Input.Control
                    type="text"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Input.Root>
              </div>

              <div className={styles.formGroup}>
                <Input.Root
                  name="accountHolderName"
                  required
                  errorMessage={errors.accountHolderName}
                >
                  <Input.Label>Account Holder Name</Input.Label>
                  <Input.Control
                    type="text"
                    value={formData.accountHolderName}
                    onChange={handleInputChange}
                    placeholder="Enter account holder name as per bank records"
                  />
                </Input.Root>
              </div>
            </div>

            <div className={styles.uploadInstructions}>
              <h4>Important Notes:</h4>
              <ul>
                <li>Ensure account holder name matches your legal name</li>
                <li>IFSC code must be in correct format (e.g., SBIN0001234)</li>
                <li>Account number should be 9-18 digits long</li>
                <li>This information will be used for payment processing</li>
              </ul>
            </div>

            <div className={styles.modalActions}>
              <Button type="button" variant="transparent" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Add Bank Details'}
              </Button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BankDetailsModal;
